import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import EditText from "../../components/editText";
import dayjs from "dayjs";
import { formatDubaiDateTime } from "../../utils/dateTime";
import {
  useAssignEmployeeMutation,
  useLazyAllEmployeesQuery,
} from "../../service/booking";
import { showError, showToast } from "../../constants/toasts";
import { useLazyGetInternalProcessQuery } from "../../service/category";
import useAuth from "../../hooks/useAuth";
import {
  BOOKING_SORTING,
  BOOKING_STATUS,
  INTERNAL_STATUS,
  OFFER_TYPE,
  USER_STATUS,
} from "../../utils/enums";
import { BookingListType } from "../../types/General";
import { isValidNumber } from "../../utils/validations";
import SwapVertIcon from "@mui/icons-material/SwapVert";

interface RsvpTableProps {
  category: string;
  statusFilter: string;
  bookingData: BookingListType[];
  getBookingsApi: (clearAll?: any, order?: string) => void;
  valueTabs: number;
  valueTabs1: number;
  categoryId: string | null;
  sortOrder: string;
  setSortOrder: (order: string) => void;
}

interface EmployeeType {
  email: string;
  id: string;
  name: string;
}

enum INTERNAL {
  IN_PROGRESS = "InProgress",
  CONFIRMED = "Confirmed",
  NEW = "New",
  CANCELLED = "Cancelled",
  COMPLETED = "Completed",
}

interface Status {
  id: string;
  name: string;
  isDeletable: boolean;
  createAt: string; // ISO date string
  updateAt: string; // ISO date string
  isActive: boolean;
}

const RsvpTable: React.FC<RsvpTableProps> = ({
  category,
  statusFilter,
  bookingData,
  getBookingsApi,
  valueTabs,
  valueTabs1,
  categoryId,
  sortOrder,
  setSortOrder,
}) => {
  const navigate = useNavigate();
  const userData = useAuth();
  const [employees] = useLazyAllEmployeesQuery();
  const [getInternalProcess] = useLazyGetInternalProcessQuery();
  const [assignEmployee] = useAssignEmployeeMutation();

  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [Employee, setEmployee] = useState("");
  const [Status, setStatus] = useState("");
  const [legal3, setLegal3] = useState<string>("");
  const [employeeData, setEmployeeData] = useState<EmployeeType[]>([]);
  const [assignToMe, setAssignToMe] = useState(true);
  const [internalData, setInternalData] = useState<Status[]>([]);
  const [notesText, setNotesText] = useState("");
  const [amount, setAmount] = useState("");

  const toggleExpand = (
    id: any,
    employeeId?: string,
    status?: string,
    contactdealerBookingAmount?: number,
    contactdealerBookingNotes?: string
  ) => {
    const internalStatus =
      status === INTERNAL.NEW
        ? "New"
        : status === INTERNAL.CANCELLED
          ? "Cancelled"
          : status === INTERNAL.CONFIRMED
            ? "Confirmed"
            : status === INTERNAL.IN_PROGRESS
              ? "In Progress"
              : status === INTERNAL.COMPLETED
                ? "Completed"
                : "";

    const employeeAssigned = employeeId;

    setNotesText("");
    setAssignToMe(employeeId === userData?.id ? true : false);
    setEmployee(employeeAssigned || "");
    setStatus(internalStatus || "");
    setExpandedRowId((prevId) => (prevId === id ? null : id));
    setAmount(String(contactdealerBookingAmount) || "");
    setLegal3(contactdealerBookingNotes || "");
  };

  const getEmployeesApi = async () => {
    try {
      const response = await employees({}).unwrap();
      if (response.status === 200) {
        const activeEmployee =
          response?.data?.filter((tag: any) => tag?.isActive === true) || [];

        setEmployeeData(activeEmployee || []);
      } else {
        setEmployeeData([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const getInternalProcessApi = async () => {
    try {
      const response = await getInternalProcess({
        search: "",
      }).unwrap();
      if (response.status === 200) {
        setInternalData(response?.data || []);
      } else {
        setInternalData([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const assignEmployeeApi = async (
    bookingId: string,
    employeeId: string,
    status: string,
    notes: string,
    dealerAmount?: string,
    dealerNotes?: string
  ) => {
    if (!assignToMe && !Employee) {
      showError("Please select employee");
      return;
    }
    if (!Status) {
      showError("Please select status");
      return;
    }

    try {
      const body = {
        bookingId: bookingId,
        employeeId: assignToMe ? userData?.id : employeeId,
        type: "booking",
        bookingStatus:
          status === INTERNAL_STATUS.NEW
            ? "New"
            : status === INTERNAL_STATUS.IN_PROGRESS
              ? "InProgress"
              : status === INTERNAL_STATUS.CANCELLED
                ? "Cancelled"
                : status === INTERNAL_STATUS.CONFIRMED
                  ? "Confirmed"
                  : status === INTERNAL_STATUS.COMPLETED
                    ? "Completed"
                    : "",
        notes: notes,
        ...(dealerAmount ? { dealerAmount: dealerAmount } : {}),
        ...(dealerNotes ? { dealerNotes: dealerNotes } : {}),
      };

      const response = await assignEmployee(body).unwrap();
      if (response.status === 200) {
        showToast(response?.message || "");
        setNotesText("");
        setAssignToMe(false);
        setEmployee("");
        setStatus("");
        getBookingsApi();
        toggleExpand(bookingId);
        setAmount("");
        setLegal3("");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getEmployeesApi();
    getInternalProcessApi();
  }, []);

  return (
    <div className="table_container">
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th>Booking ID</th>
            <th style={{}}>
              <div
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <p style={{ color: "white" }}>Client Detail</p>
                <SwapVertIcon
                  onClick={() =>
                    setSortOrder(
                      sortOrder === BOOKING_SORTING.ClientDetail_asc
                        ? "ClientDetail_desc"
                        : "ClientDetail_asc"
                    )
                  }
                />
              </div>
            </th>
            <th>
              <div
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <p style={{ color: "white" }}>Offer Name</p>
                <SwapVertIcon
                  onClick={() =>
                    setSortOrder(
                      sortOrder === BOOKING_SORTING.OfferName_asc
                        ? "OfferName_desc"
                        : "OfferName_asc"
                    )
                  }
                />
              </div>
            </th>
            <th>
              <div
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Booking Time
                <SwapVertIcon
                  onClick={() =>
                    setSortOrder(
                      sortOrder === BOOKING_SORTING.BookingTime_asc
                        ? "BookingTime_desc"
                        : "BookingTime_asc"
                    )
                  }
                />
              </div>
            </th>
            <th>
              <div
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Date Created
                <SwapVertIcon
                  onClick={() =>
                    setSortOrder(
                      sortOrder === BOOKING_SORTING.DateCreated_asc
                        ? "DateCreated_desc"
                        : "DateCreated_asc"
                    )
                  }
                />
              </div>
            </th>
            <th>Guests</th>
            <th>
              <div
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Category
                <SwapVertIcon
                  onClick={() =>
                    setSortOrder(
                      sortOrder === BOOKING_SORTING.Category_asc
                        ? "Category_desc"
                        : "Category_asc"
                    )
                  }
                />
              </div>
            </th>
            <th>Employee Name</th>
            <th>
              <div
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Internal Status
                <SwapVertIcon
                  onClick={() =>
                    setSortOrder(
                      sortOrder === BOOKING_SORTING.Status_asc
                        ? "Status_desc"
                        : "Status_asc"
                    )
                  }
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {bookingData?.length ? (
            bookingData?.map((row) => (
              <React.Fragment key={row.id}>
                <tr>
                  <td
                    className="cursor_p"
                    onClick={() =>
                      navigate(
                        `/manage-booking-resvp/booking-detail/${row?.id}`,
                        {
                          state: {
                            count: row?.numberOfGuest,
                            status: valueTabs1,
                            categoryTab: valueTabs,
                            categoryId: categoryId,
                            isPast:
                              row?.status === INTERNAL_STATUS.COMPLETED
                                ? true
                                : false,
                            internalStatus: row?.status,
                          },
                        }
                      )
                    }
                  >
                    {row?.bookingId || "-"}
                  </td>
                  <td
                    className="cursor_p max_c2"
                    onClick={() => {
                      if (row?.userName) {
                        navigate(
                          `/manage-customers/detail/${row?.userName?.id}`,
                          {
                            state: {
                              status:
                                row?.userName?.userStatus ===
                                USER_STATUS.Approved
                                  ? "APPROVED"
                                  : row?.userName?.userStatus ===
                                      USER_STATUS.Rejected
                                    ? "REJECTED"
                                    : row?.userName?.userStatus ===
                                        USER_STATUS.Expired
                                      ? "EXPIRED"
                                      : row?.userName?.userStatus ===
                                          USER_STATUS.Cancelled
                                        ? "CANCELLED"
                                        : "PENDING",
                              memberType: row?.userName?.memberShip,
                              subTab: valueTabs1,
                              categoryTab: valueTabs,
                              categoryId: categoryId,
                              comingFrom: "booking",
                            },
                          }
                        );
                      }
                    }}
                  >
                    {row?.userName?.name || "-"}
                    <br />
                    <small>{`${row?.userName?.phoneNumber?.countryDialCode ? `+${row?.userName?.phoneNumber?.countryDialCode}` : ""} ${row?.userName?.phoneNumber?.phone || "-"}`}</small>
                  </td>
                  <td
                    className="cursor_p max_c2"
                    onClick={() =>
                      navigate(
                        `/manage-offer/preview-offer-detail/${row?.offerId}`,
                        {
                          state: {
                            type:
                              row?.offerType === "event"
                                ? "EVENT"
                                : row?.offerType === "experience"
                                  ? "EXPERIENCE"
                                  : "RESTAURANT",
                          },
                        }
                      )
                    }
                  >
                    {row?.title || "-"}
                  </td>
                  <td className="max_c2">
                    <p
                      onClick={() =>
                        toggleExpand(
                          row.id,
                          row?.employeeName?.employeeId,
                          row?.status,
                          row?.contactdealerBookingAmount,
                          row?.contactdealerBookingNotes
                        )
                      }
                      className="text_btn cursor_p"
                    >
                      {row?.bookingDate
                        ? formatDubaiDateTime(row?.bookingDate)
                        : "-"}
                    </p>
                  </td>

                  <td className="max_c2">
                    {row?.createAt ? formatDubaiDateTime(row?.createAt) : "-"}
                  </td>

                  <td>{row?.totalNumberofGuest || "0"}</td>

                  <td className="max_c2"> {row?.categoryName || "-"}</td>
                  <td className="max_c2">{row?.employeeName?.name || "-"}</td>
                  <td>
                    {row?.status === BOOKING_STATUS.PENDING
                      ? "In Progress"
                      : row?.status}
                  </td>
                </tr>

                {expandedRowId === row?.id && (
                  <tr>
                    <td colSpan={8}>
                      <div className="expanded-content">
                        <div className="form_flex">
                          <div className="w_100">
                            <div className="control_group mb_0 employee_list">
                              <Select
                                value={Employee}
                                onChange={(e: SelectChangeEvent) =>
                                  setEmployee(e.target.value)
                                }
                                displayEmpty
                              >
                                <MenuItem value="" disabled>
                                  Employee Listing
                                </MenuItem>
                                {employeeData?.map((item) => {
                                  return (
                                    <MenuItem key={item?.id} value={item?.id}>
                                      {item?.name || "-"}
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </div>

                            <Select
                              value={Status}
                              onChange={(e: SelectChangeEvent) =>
                                setStatus(e.target.value)
                              }
                              displayEmpty
                              className="booking_status"
                              disabled={Status === "Completed"}
                              sx={{
                                "& .MuiSelect-select.Mui-disabled": {
                                  color: "rgba(255,255,255,0.3)",
                                  WebkitTextFillColor: "rgba(255,255,255,0.3)",
                                },
                              }}
                            >
                              <MenuItem value="" disabled>
                                Status
                              </MenuItem>
                              {internalData?.map((item) => {
                                return (
                                  <MenuItem key={item?.id} value={item?.name}>
                                    {item?.name || "-"}
                                  </MenuItem>
                                );
                              })}
                            </Select>

                            <div className="control_group add_note">
                              <TextField
                                placeholder="Add Notes"
                                hiddenLabel
                                fullWidth
                                value={notesText}
                                inputProps={{ maxLength: 150 }}
                                onChange={(e) => {
                                  if (
                                    e.target.value === " " ||
                                    e.target.value === "."
                                  ) {
                                  } else {
                                    setNotesText(e.target.value);
                                  }
                                }}
                              />
                            </div>

                            <Button
                              onClick={() =>
                                assignEmployeeApi(
                                  row?.id,
                                  Employee,
                                  Status,
                                  notesText,
                                  amount,
                                  legal3
                                )
                              }
                              className="btn btn_primary"
                            >
                              Save
                            </Button>
                          </div>

                          <div className="w_100">
                            <Checkbox
                              checked={assignToMe}
                              onChange={(e) => {
                                setAssignToMe(e.target.checked);
                                if (e.target.checked) {
                                  setEmployee(userData?.id || "");
                                } else {
                                  setEmployee("");
                                }
                              }}
                            />
                            Assign to me
                          </div>

                          {row.offerType === OFFER_TYPE.CONTACT_DEALERS && (
                            <>
                              <div
                                className="w_100"
                                style={{ alignItems: "flex-start" }}
                              >
                                <div className="control_group w_50">
                                  <TextField
                                    placeholder="Amount"
                                    hiddenLabel
                                    fullWidth
                                    value={amount}
                                    inputProps={{ maxLength: 10 }}
                                    onChange={(e) => {
                                      const { value } = e.target;
                                      if (isValidNumber(value)) {
                                        setAmount(value);
                                      }
                                    }}
                                  />
                                </div>
                                <div className="control_group w_50">
                                  <div className="control_group w_100">
                                    <EditText
                                      content={legal3}
                                      setContent={setLegal3}
                                    />
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          ) : (
            <tr>
              <td colSpan={8} style={{ textAlign: "center", padding: "20px" }}>
                No Data Found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default RsvpTable;
